// ./src/components/features/SignupItems/SignupItemsList/SignupItemsList.tsx
//
// Event Signup-list items list — table on desktop (>= md), cards on tablet/mobile.
// Tap-safe (explicit Edit/Delete only), rights-gated per row, selection-ready.
// Category name + icon/color are resolved from the shared CategoriesContext. Mirrors
// the Accommodations list conventions (see em-scaffold-ui).

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  Badge,
  ThemeIcon,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
  Checkbox,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { STATUSES } from "@sdflc/utils";

import { SignupItem, signupItemMainImageUrl } from "@/utils/api";
import { UpvoteCountBadge } from "@/components/features/Upvotes/UpvoteCountBadge";
import { RowSelection } from "@/hooks";
import { useAppEnvironment } from "@/contexts";
import { useOptionalSignupItemsContext } from "@/contexts/SignupItemsContext";
import { useOptionalCategoriesContext } from "@/contexts/CategoriesContext";
import {
  AppIcon,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  CardSkeletonCheckbox,
  countCardActions,
  type CardSkeletonShape,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import { useEntityCommentsDrawer } from "@/components/features/Comments";
import { recordHasRight, HIGHLIGHT_STYLE, HIGHLIGHT_ROW_BG, formatCurrency } from "@/utils";

// ============================================
// Props
// ============================================

/** A category section for the grouped list (heading + its items, already ordered). */
export interface SignupItemGroup {
  key: string;
  label: string;
  icon?: string | null;
  color?: string | null;
  items: SignupItem[];
}

export interface SignupItemsListProps {
  data?: SignupItem[];
  /**
   * Render items grouped under category headings (order = as given). When set, it
   * drives the rows; `data`/context items are ignored for layout (the flat list is
   * derived from the groups for selection + empty-state logic).
   */
  groups?: SignupItemGroup[];
  loading?: boolean;
  loadingMore?: boolean;
  hasMore?: boolean;
  onLoadMore?: () => void;
  onEditSignupItem?: (item: SignupItem) => void;
  onDeleteSignupItem?: (item: SignupItem) => void;
  /** Opens the "Sign up guests" drawer for an item (host/crew sign guests up on their behalf). */
  onManageSignups?: (item: SignupItem) => void;
  isRemoving?: (id?: string) => boolean;
  highlightedId?: string | null;
  onAddSignupItem?: () => void;
  selection?: RowSelection;
  selectable?: boolean;
  /** True when a search term or filter is narrowing the list — shows a "no match" message instead of the empty state. */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

const isDisabled = (a: SignupItem): boolean => a.status === STATUSES.DISABLED;

/** A row is editable when the caller has any right on it (BE sets [] for read-only rows). */
const isEditableRow = (a: SignupItem): boolean =>
  recordHasRight(a, "edit") || recordHasRight(a, "remove");

/** `price (in its currency) / priceUnit`, dropping any part that isn't set. */
const formatPrice = (a: SignupItem): string | null => {
  if (a.price === null || a.price === undefined) {
    return null;
  }

  // #A4.7b — formatted in the EVENT's currency (every row carries it), e.g. "CA$120.00".
  const amount = a.currencyCode ? formatCurrency(Number(a.price), a.currencyCode) : `${a.price}`;

  return a.priceUnit ? `${amount} / ${a.priceUnit}` : amount;
};

interface CategoryMeta {
  name: string | null;
  icon: string | null;
  color: string | null;
}

const StatusBadge: React.FC<{ a: SignupItem }> = ({ a }) => {
  const { t } = useTranslation(["common"]);

  if (!isDisabled(a)) {
    return null;
  }

  return (
    <Badge size="xs" variant="light" color="gray">
      {t("common:status.disabled", { defaultValue: "Disabled" })}
    </Badge>
  );
};

const CategoryCell: React.FC<{ meta: CategoryMeta | null }> = ({ meta }) => {
  if (!meta?.name) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Group gap={6} wrap="nowrap">
      {meta.icon ? <AppIcon iconName={meta.icon} size={16} color={meta.color || undefined} /> : null}
      <Text size="md" c={meta.color || undefined} lineClamp={1}>
        {meta.name}
      </Text>
    </Group>
  );
};

const SignupItemIcon: React.FC<{ meta: CategoryMeta | null; imageUrl?: string | null; size?: number }> = ({
  meta,
  imageUrl,
  size = 34,
}) => {
  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        style={{ width: size, height: size, objectFit: "cover", borderRadius: "var(--mantine-radius-md)", display: "block", flexShrink: 0 }}
      />
    );
  }

  return (
    <ThemeIcon variant="light" color={meta?.color || "grape"} radius="xl" size={size}>
      <AppIcon iconName={meta?.icon || "ClipboardList"} size={Math.round(size * 0.55)} />
    </ThemeIcon>
  );
};

const RowActions: React.FC<{
  a: SignupItem;
  onEdit?: (a: SignupItem) => void;
  onDelete?: (a: SignupItem) => void;
  onManageSignups?: (a: SignupItem) => void;
  onOpenComments?: (a: SignupItem) => void;
  isDeleting: boolean;
}> = ({ a, onEdit, onDelete, onManageSignups, onOpenComments, isDeleting }) => {
  const { t } = useTranslation(["events", "common"]);

  const showManage = !!onManageSignups && recordHasRight(a, "edit");
  const showEdit = !!onEdit && recordHasRight(a, "edit");
  const showDelete = !!onDelete && recordHasRight(a, "remove");
  const showComments = !!onOpenComments; // comments are open to any viewer of the item

  if (!showManage && !showEdit && !showDelete && !showComments) {
    return null;
  }

  return (
    <Group gap={4} wrap="nowrap">
      {showComments && onOpenComments && (
        <Tooltip label={t("common:comments.action", { defaultValue: "Comments" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="md"
            onClick={() => onOpenComments(a)}
            disabled={isDeleting}
            aria-label={t("common:comments.action", { defaultValue: "Comments" })}
          >
            <AppIcon iconName="Message" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showManage && onManageSignups && (
        <Tooltip label={t("events:signupItems.signups.action", { defaultValue: "Sign up guests" })} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="grape"
            size="md"
            onClick={() => onManageSignups(a)}
            disabled={isDeleting}
            aria-label={t("events:signupItems.signups.action", { defaultValue: "Sign up guests" })}
          >
            <AppIcon iconName="Users" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(a)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(a)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Row shape
// ============================================

interface RowProps {
  a: SignupItem;
  name: string;
  meta: CategoryMeta | null;
  imageUrl: string | null;
  price: string | null;
  quantity: string;
  maxPerGuest: string;
  editable: boolean;
  disabled: boolean;
  onEdit?: (a: SignupItem) => void;
  onDelete?: (a: SignupItem) => void;
  onManageSignups?: (a: SignupItem) => void;
  onOpenComments?: (a: SignupItem) => void;
  highlighted: boolean;
  isDeleting: boolean;
  selectionColumn: boolean;
  selectable: boolean;
  selected: boolean;
  selectionMode: boolean;
  onToggleSelect?: (id: string) => void;
}

// ============================================
// Desktop table row
// ============================================

const SignupItemTableRow: React.FC<RowProps> = ({
  a,
  name,
  meta,
  imageUrl,
  price,
  quantity,
  maxPerGuest,
  disabled,
  onEdit,
  onDelete,
  onManageSignups,
  onOpenComments,
  highlighted,
  isDeleting,
  selectionColumn,
  selectable,
  selected,
  onToggleSelect,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : disabled ? 0.6 : 1,
        backgroundColor: highlighted
          ? HIGHLIGHT_ROW_BG
          : selected
          ? "var(--mantine-color-blue-light)"
          : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      {selectionColumn && (
        <Table.Td style={{ width: 44 }}>
          {selectable && (
            <Checkbox
              checked={selected}
              onChange={() => a.id && onToggleSelect?.(a.id)}
              disabled={isDeleting}
              aria-label={name}
            />
          )}
        </Table.Td>
      )}
      <Table.Td>
        <Group gap="sm" wrap="nowrap">
          <SignupItemIcon meta={meta} imageUrl={imageUrl} />
          <Box style={{ minWidth: 0 }}>
            <Group gap={6} wrap="nowrap">
              <Text size="md" fw={600} lineClamp={1}>
                {name}
              </Text>
              <StatusBadge a={a} />
              <UpvoteCountBadge count={a.upvoteCount} />
            </Group>
          </Box>
        </Group>
      </Table.Td>
      <Table.Td style={{ width: 180 }}>
        <CategoryCell meta={meta} />
      </Table.Td>
      <Table.Td style={{ width: 130 }}>
        <Text size="md" lineClamp={1}>
          {quantity}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 130 }}>
        <Text size="sm" c="dimmed" lineClamp={1}>
          {maxPerGuest}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 160 }}>
        <Text size="md" lineClamp={1}>
          {price || "—"}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 100 }}>
        <RowActions a={a} onEdit={onEdit} onDelete={onDelete} onManageSignups={onManageSignups} onOpenComments={onOpenComments} isDeleting={isDeleting} />
      </Table.Td>
    </Table.Tr>
  );
};

const SignupItemTableRowSkeleton: React.FC<{ selectionColumn?: boolean }> = ({ selectionColumn }) => (
  <Table.Tr>
    {selectionColumn && (
      <Table.Td style={{ width: 44 }}>
        <Skeleton height={20} width={20} radius="sm" />
      </Table.Td>
    )}
    <Table.Td>
      <Group gap="sm" wrap="nowrap">
        <Skeleton circle height={34} width={34} />
        <Skeleton height={14} width="40%" maw={200} />
      </Group>
    </Table.Td>
    <Table.Td style={{ width: 180 }}>
      <Skeleton height={14} width={120} />
    </Table.Td>
    <Table.Td style={{ width: 130 }}>
      <Skeleton height={14} width={70} />
    </Table.Td>
    <Table.Td style={{ width: 130 }}>
      <Skeleton height={14} width={70} />
    </Table.Td>
    <Table.Td style={{ width: 160 }}>
      <Skeleton height={14} width={100} />
    </Table.Td>
    <Table.Td style={{ width: 100 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Card (tablet / mobile)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const SignupItemCardComponent: React.FC<CardProps> = ({
  a,
  name,
  meta,
  imageUrl,
  price,
  quantity,
  disabled,
  onEdit,
  onDelete,
  onManageSignups,
  onOpenComments,
  highlighted,
  isDeleting,
  selectable,
  selected,
  selectionMode,
  onToggleSelect,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["events", "common"]);

  const toggleSelect = () => a.id && onToggleSelect?.(a.id);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];

    // Secondary actions (comments + manage-signups) auto-route to the LEFT swipe side; edit/remove stay right.
    if (onOpenComments) {
      actions.push({
        key: "comments",
        icon: "Message",
        label: t("common:comments.action", { defaultValue: "Comments" }),
        color: "gray",
        onClick: () => onOpenComments(a),
        disabled: isDeleting,
        secondary: true,
      });
    }

    if (onManageSignups && recordHasRight(a, "edit")) {
      actions.push({
        key: "signups",
        icon: "Users",
        label: t("events:signupItems.signups.action", { defaultValue: "Sign up guests" }),
        color: "grape",
        onClick: () => onManageSignups(a),
        disabled: isDeleting,
        secondary: true,
      });
    }

    if (onEdit && recordHasRight(a, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(a),
        disabled: isDeleting,
      });
    }

    if (onDelete && recordHasRight(a, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(a),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }

    return actions;
  }, [onEdit, onDelete, onManageSignups, onOpenComments, a, isDeleting, t]);

  const cardBody = (showActions: boolean, showCheckbox: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : disabled ? 0.6 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        backgroundColor: selected ? "var(--mantine-color-blue-light)" : undefined,
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="flex-start">
        {showCheckbox && (
          <Checkbox
            mt={2}
            checked={selected}
            onChange={toggleSelect}
            onClick={(e) => e.stopPropagation()}
            disabled={isDeleting}
            aria-label={name}
          />
        )}
        <SignupItemIcon meta={meta} imageUrl={imageUrl} size={40} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            <Text size="md" fw={600} lineClamp={1}>
              {name}
            </Text>
            <StatusBadge a={a} />
            <UpvoteCountBadge count={a.upvoteCount} />
          </Group>
          <Box mt={4}>
            <CategoryCell meta={meta} />
          </Box>
          <Group gap={8} wrap="nowrap" mt={4}>
            <Text size="sm" c="dimmed">
              {t("events:signupItems.list.qtyShort", { defaultValue: "Qty: {{qty}}", qty: quantity })}
            </Text>
            {price && <Text size="md">· {price}</Text>}
          </Group>
        </Box>
        {showActions && <RowActions a={a} onEdit={onEdit} onDelete={onDelete} onManageSignups={onManageSignups} onOpenComments={onOpenComments} isDeleting={isDeleting} />}
      </Group>
    </Paper>
  );

  if (selectionMode) {
    return (
      <>
        <Box
          hiddenFrom="sm"
          onClick={selectable ? toggleSelect : undefined}
          style={{ cursor: selectable ? "pointer" : "default", userSelect: "none" }}
        >
          {cardBody(false, selectable)}
        </Box>
        <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
      </>
    );
  }

  return (
    <>
      <Box hiddenFrom="sm">
        {swipeActions.length > 0 ? (
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false, false)}
          </SwipeableCard>
        ) : (
          cardBody(false, false)
        )}
      </Box>
      <Box visibleFrom="sm">{cardBody(true, selectable)}</Box>
    </>
  );
};

const SignupItemCard = React.memo(SignupItemCardComponent);

const SignupItemCardSkeleton: React.FC<CardSkeletonShape> = ({ actions, withCheckbox }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="flex-start">
      <CardSkeletonCheckbox show={withCheckbox} />
      <Skeleton circle height={40} width={40} />
      <CardSkeletonLines count={3} sizes={["md", "md", "sm"]} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main
// ============================================

const SignupItemsListComponent: React.FC<SignupItemsListProps> = ({
  data: dataProp,
  groups,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditSignupItem,
  onDeleteSignupItem,
  onManageSignups,
  isRemoving: isRemovingProp,
  highlightedId,
  onAddSignupItem,
  selection,
  selectable = false,
  hasQueryNarrowing = false,
}) => {
  const { t } = useTranslation(["events", "common"]);
  const { isDesktop } = useAppEnvironment();

  const isSelectable = selectable && !!selection;
  // The tablet card's row buttons, one per handler — what the card skeleton draws (▶ check-card-skeletons).
  const cardActions = countCardActions(onManageSignups, onEditSignupItem, onDeleteSignupItem, true /* comments — open to every viewer */);

  const ctx = useOptionalSignupItemsContext();
  const categoriesContext = useOptionalCategoriesContext();

  // When grouped, the flat list (for selection, skeleton, empty-state) is derived
  // from the groups so callers pass only `groups`.
  const signupItems = groups ? groups.flatMap((g) => g.items) : dataProp ?? ctx?.signupItems ?? [];
  const loading = loadingProp ?? ctx?.loading ?? false;
  const loadingMore = loadingMoreProp ?? ctx?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? ctx?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? ctx?.loadMore;
  const isRemoving = isRemovingProp ?? ctx?.isRemoving ?? (() => false);

  const { openCard, closeAllCards, shouldForceClose, handleForceCloseComplete } = useSwipeableList();
  const { hasAnimated } = useEntranceAnimation({ itemCount: signupItems.length });

  // Comments row action — opens the reusable drawer for any viewer, without the edit drawer. Mounted once
  // (below) via `comments.drawer`; each row calls `comments.open(...)` from its Comments action.
  const comments = useEntityCommentsDrawer();

  const formatQuantity = useCallback(
    (a: SignupItem): string =>
      a.quantityAvailable === null || a.quantityAvailable === undefined
        ? t("events:signupItems.list.unlimited", { defaultValue: "Unlimited" })
        : String(a.quantityAvailable),
    [t]
  );

  const formatMaxPerGuest = useCallback(
    (a: SignupItem): string =>
      a.maxQuantityPerGuest === null || a.maxQuantityPerGuest === undefined
        ? "—"
        : String(a.maxQuantityPerGuest),
    []
  );

  const categoryMeta = useCallback(
    (a: SignupItem): CategoryMeta | null => {
      if (!a.categoryId) {
        return null;
      }

      const category = categoriesContext?.getById(a.categoryId);

      if (!category) {
        return null;
      }

      return {
        name: category.name || category.code || null,
        icon: category.icon ?? null,
        color: category.color ?? null,
      };
    },
    [categoriesContext]
  );

  const handleEdit = useCallback(
    (a: SignupItem) => {
      closeAllCards();
      onEditSignupItem?.(a);
    },
    [onEditSignupItem, closeAllCards]
  );

  const handleDelete = useCallback(
    (a: SignupItem) => {
      closeAllCards();
      onDeleteSignupItem?.(a);
    },
    [onDeleteSignupItem, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (id: string, isOpen: boolean) => {
      if (isOpen) {
        openCard(id);
      }
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = ctx?.isLoaded ?? (groups !== undefined || dataProp !== undefined);
  const showSkeleton = !isLoaded;

  if (!showSkeleton && signupItems.length === 0) {
    // A narrowed list that came back empty is a "no match", not a truly-empty event —
    // don't offer "Add item" (the fix is to clear the search/filters, not to add one).
    if (hasQueryNarrowing) {
      return (
        <Paper withBorder p="xl" radius="md">
          <EmptyState
            title={t("events:signupItems.noMatch.title", { defaultValue: "No items match" })}
            description={t("events:signupItems.noMatch.description", {
              defaultValue: "Try a different search or clear the filters.",
            })}
            iconName="Search"
            size="md"
          />
        </Paper>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("events:signupItems.emptyState.title", { defaultValue: "No signup items yet" })}
          description={t("events:signupItems.emptyState.description", {
            defaultValue: "Add things guests can sign up to bring or do — potluck dishes, volunteer shifts, supplies, merch or donations.",
          })}
          iconName="ClipboardList"
          actionLabel={t("events:signupItems.emptyState.actionLabel", { defaultValue: "Add item" })}
          onAction={onAddSignupItem}
          size="md"
        />
      </Paper>
    );
  }

  const rowIsSelectable = (a: SignupItem): boolean =>
    isSelectable &&
    ((!!onEditSignupItem && recordHasRight(a, "edit")) ||
      (!!onDeleteSignupItem && recordHasRight(a, "remove")));

  const rowProps = (a: SignupItem): RowProps => ({
    a,
    name: a.name || "—",
    meta: categoryMeta(a),
    imageUrl: signupItemMainImageUrl(a),
    price: formatPrice(a),
    quantity: formatQuantity(a),
    maxPerGuest: formatMaxPerGuest(a),
    editable: isEditableRow(a),
    disabled: isDisabled(a),
    onEdit: onEditSignupItem ? handleEdit : undefined,
    onDelete: onDeleteSignupItem ? handleDelete : undefined,
    onManageSignups,
    onOpenComments: (item) =>
      comments.open({ eventId: item.eventId ?? "", entityKind: "signup", entityId: item.id ?? "", title: item.name ?? undefined }),
    highlighted: a.id === highlightedId,
    isDeleting: isRemoving(a.id),
    selectionColumn: isSelectable,
    selectable: rowIsSelectable(a),
    selected: selection?.isSelected(a.id) ?? false,
    selectionMode: selection?.selectionMode ?? false,
    onToggleSelect: selection?.toggle,
  });

  const selectableVisibleIds = signupItems
    .filter((a) => rowIsSelectable(a))
    .map((a) => a.id)
    .filter((id): id is string => !!id);

  const loadMore = (
    <LoadMoreButton hasMore={hasMore} loading={loading} loadingMore={loadingMore} onLoadMore={onLoadMore} />
  );

  // ── Desktop table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                {isSelectable && (
                  <Table.Th style={{ width: 44 }}>
                    <Checkbox
                      checked={selection!.allSelected(selectableVisibleIds)}
                      indeterminate={selection!.someSelected(selectableVisibleIds)}
                      onChange={() => selection!.toggleAll(selectableVisibleIds)}
                      disabled={selectableVisibleIds.length === 0}
                      aria-label={t("common:selection.selectAll", { defaultValue: "Select all" })}
                    />
                  </Table.Th>
                )}
                <Table.Th>{t("events:signupItems.list.columns.name", { defaultValue: "Item" })}</Table.Th>
                <Table.Th style={{ width: 180 }}>
                  {t("events:signupItems.list.columns.category", { defaultValue: "Category" })}
                </Table.Th>
                <Table.Th style={{ width: 130 }}>
                  {t("events:signupItems.list.columns.quantity", { defaultValue: "Quantity" })}
                </Table.Th>
                <Table.Th style={{ width: 130 }}>
                  {t("events:signupItems.list.columns.maxPerGuest", { defaultValue: "Max / guest" })}
                </Table.Th>
                <Table.Th style={{ width: 160 }}>
                  {t("events:signupItems.list.columns.price", { defaultValue: "Price" })}
                </Table.Th>
                <Table.Th style={{ width: 100 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <SignupItemTableRowSkeleton key={i} selectionColumn={isSelectable} />
                ))
              ) : groups ? (
                groups.map((g) => (
                  <React.Fragment key={g.key}>
                    <Table.Tr>
                      <Table.Td
                        colSpan={(isSelectable ? 1 : 0) + 6}
                        style={{ background: "var(--mantine-color-default-hover)" }}
                      >
                        <Group gap="xs" wrap="nowrap">
                          {g.icon ? <AppIcon iconName={g.icon} size={16} color={g.color ?? undefined} /> : null}
                          <Text fw={700} size="sm" tt="uppercase" c="dimmed">
                            {g.label}
                          </Text>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                    {g.items.map((a) => (
                      <SignupItemTableRow key={a.id} {...rowProps(a)} />
                    ))}
                  </React.Fragment>
                ))
              ) : (
                signupItems.map((a) => <SignupItemTableRow key={a.id} {...rowProps(a)} />)
              )}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
        {comments.drawer}
      </Stack>
    );
  }

  // ── Tablet / mobile cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 6 }).map((_, i) => (
          <SignupItemCardSkeleton key={i} actions={cardActions} withCheckbox={isSelectable} />
        ))}
      </Stack>
    );
  }

  const renderCard = (a: SignupItem) => (
    <SignupItemCard
      {...rowProps(a)}
      forceClose={shouldForceClose(a.id || "")}
      onForceCloseComplete={() => handleForceCloseComplete(a.id || "")}
      onSwipeStateChange={(isOpen) => handleSwipeStateChange(a.id || "", isOpen)}
    />
  );

  // Grouped mobile: a heading per category, then its cards. Entrance animation is
  // skipped here (the section headings make the ordering clear without it).
  if (groups) {
    return (
      <Stack gap="lg">
        {groups.map((g) => (
          <Stack key={g.key} gap="sm">
            <Group gap="xs" wrap="nowrap" px={4}>
              {g.icon ? <AppIcon iconName={g.icon} size={16} color={g.color ?? undefined} /> : null}
              <Text fw={700} size="sm" tt="uppercase" c="dimmed">
                {g.label}
              </Text>
            </Group>
            {g.items.map((a) => (
              <div key={a.id}>{renderCard(a)}</div>
            ))}
          </Stack>
        ))}
        {loadMore}
        {comments.drawer}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}
      >
        <AnimatePresence mode="sync">
          {signupItems.map((a) => (
            <motion.div
              key={a.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              {renderCard(a)}
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
      {comments.drawer}
    </Stack>
  );
};

export const SignupItemsList = React.memo(SignupItemsListComponent);
